import { getAccessToken, getStepUpHandler } from '../store/authStore'

/**
 * Every HTTP request this portal makes goes through {@link request}, in this file.
 *
 * The calls themselves are split by domain beside it (`devices.ts`, `jobs.ts`, …), and
 * `client.ts` re-exports them all so that `import … from '../api/client'` keeps working.
 *
 * **All requests are built in `src/api/*.ts`, and that is a rule rather than a convention.** The
 * desktop client's route inventory (`SupportClientRouteInventoryTests`) reads its client and asserts
 * that no HTTP call is built anywhere else, because an inventory that misses a call site reports
 * complete coverage of a surface it cannot see — the server's own inventory grew from "all 148
 * arms" to 176 across four such blind spots, and the arms it had missed were disproportionately
 * the ones whose authorization had been written differently, or not at all. The same check
 * (`PortalClientRouteInventoryTests`) reads every `src/api/*.ts` here, and allows `fetch` only in
 * this file. WebSocket sessions are deliberately out of scope: the viewers open their own sockets
 * and those are not HTTP.
 *
 * The shapes in these modules are *wire* shapes — snake_case, matching the relay's
 * `JsonNamingPolicy.SnakeCaseLower`. Where a Portal type matches the relay's OpenAPI schema it is an
 * alias of the generated one (`schema.ts`). They are not renamed to camelCase here: a fixture or a type
 * written in the shape the server never sends is the exact failure that typed clients are supposed
 * to prevent.
 *
 * **Docker is the exception, and it is not a mistake.** `Relay.Protocol/DockerMessages.cs` annotates
 * every member with an explicit `[JsonPropertyName("Id")]`, `("Containers")`, `("CpuPercent")` and
 * so on — 58 of them — and an explicit attribute beats the serializer's naming policy. Those
 * payloads are forwarded from the agent rather than reshaped by the relay, so they arrive
 * PascalCase. The desktop client reached the same conclusion independently.
 *
 * Paths are written as literal template strings with the verb adjacent and literal, so the route
 * ratchet can read them the way it reads `RelayApi.cs`. Keep them that way: a path assembled from
 * variables is a call the inventory stops seeing, and it fails silently. For the same reason there
 * is exactly one request helper — the ratchet finds a verb by matching `request<T>('VERB'`, so a
 * second helper under another name would render its calls verb-less and quietly stop being checked.
 * Bodies and response kinds are therefore options on `request`, not separate functions.
 */

/**
 * The filename from a `Content-Disposition` header.
 *
 * `filename*=UTF-8''…` is preferred when present — it is the encoded form, and the plain `filename`
 * beside it is a deliberately lossy ASCII fallback for old clients.
 */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim())
    } catch {
      // A malformed encoding is not a reason to fail a download that otherwise succeeded.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain ? plain[1].trim() : null
}

// ── The one request builder ──────────────────────────────────────────────────────────────────────

/**
 * A non-2xx response. Carries the status so a caller can tell 403 from 404 from 409, and — from a
 * relay that answers with problem details (docs/API-Errors.md) — the stable `code` to switch on and
 * the request's id to quote to support.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly statusText: string,
    readonly url: string,
    readonly method: string,
    /** The relay's own sentence for a person: `detail`, else the legacy `error`, else `message`. */
    readonly detail?: string,
    /** The relay's stable reason (`device_offline`, `access_denied`, …); absent from an older relay. */
    readonly code?: string,
    /**
     * The request's id as the relay confirmed it: the body's `request_id`, else the echoed
     * `X-Request-Id` header. One the relay never confirmed is not kept, so a reference shown to an
     * operator always finds the request in the relay's log.
     */
    readonly requestId?: string,
    /**
     * For a step-up refusal (`step_up_required`): the authentication strength to sign in again with, from the
     * relay's `WWW-Authenticate` challenge (RFC 9470), or null for any.
     */
    readonly acrValues: string | null = null,
  ) {
    super(`${method} ${url} failed: ${status} ${statusText}`)
    this.name = 'ApiError'
  }
}

/**
 * The relay did not answer within the request's timeout.
 *
 * A subclass of {@link ApiError} so every `instanceof ApiError` check still treats it as a failed
 * call, with status 0 because there was no response to take one from. It is told apart from a 408
 * on purpose: the relay answers 408 when a *device* did not answer it, while this is the relay
 * itself not answering the browser, and the two need different words.
 */
export class ApiTimeoutError extends ApiError {
  constructor(url: string, method: string, readonly timeoutMs: number) {
    super(0, 'Timeout', url, method)
    this.message = `${method} ${url} timed out after ${timeoutMs} ms`
    this.name = 'ApiTimeoutError'
  }
}

/**
 * How long an ordinary request may take before it is abandoned.
 *
 * Thirty seconds is long past anything the relay legitimately takes for a JSON call (its own agent
 * round trips time out at 30 s and answer 408), so reaching it means the relay or the network is
 * stuck, and a page waiting forever on a spinner is the worse outcome.
 */
export const DEFAULT_TIMEOUT_MS = 30_000

/**
 * One signal that aborts when any of `signals` does.
 *
 * `AbortSignal.any` where the browser has it (every current one), and a hand-wired controller
 * where it does not, so an older embedded browser still honours both the caller's cancellation and
 * the timeout.
 */
export function anySignal(signals: AbortSignal[]): AbortSignal {
  if (signals.length === 1) return signals[0]
  const native = (AbortSignal as { any?: (s: AbortSignal[]) => AbortSignal }).any
  if (typeof native === 'function') return native.call(AbortSignal, signals)

  const controller = new AbortController()
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason)
      break
    }
    signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true })
  }
  return controller.signal
}

/** The header a request's id travels in, both ways. */
export const REQUEST_ID_HEADER = 'X-Request-Id'

/**
 * A new request id: 32 hex digits, the shape the relay generates its own in.
 *
 * Sent on every request so that the relay logs it under an id this side chose, and echoes it back.
 */
export function newRequestId(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** What a relay error body says. Every member is optional: an older relay sent `{error}` alone. */
export interface RelayProblem {
  code?: string
  detail?: string
  requestId?: string
}

function text(record: Record<string, unknown>, name: string): string | undefined {
  const value = record[name]
  return typeof value === 'string' && value !== '' ? value : undefined
}

/**
 * Reads a relay error body: problem details (`{title, status, detail, code, request_id, error}`), a
 * gateway refusal (`{code, message, error}`), or an older relay's `{error}`.
 *
 * Only string members count. Anything else — an array, a bare string, an HTML page from a proxy —
 * reads as nothing, and the caller falls back to wording by status: showing a proxy's page verbatim
 * would be worse than that. The same rule as Support's `RelayProblem.Parse`.
 */
export function parseProblem(body: unknown): RelayProblem {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return {}
  const record = body as Record<string, unknown>
  return {
    code: text(record, 'code'),
    detail: text(record, 'detail') ?? text(record, 'error') ?? text(record, 'message'),
    requestId: text(record, 'request_id'),
  }
}

/** A failed response, read into an {@link ApiError}. Never throws. */
async function failedResponse(response: Response, url: string, method: string): Promise<ApiError> {
  let problem: RelayProblem = {}
  try {
    problem = parseProblem(await response.json())
  } catch {
    // Not JSON: worded by status.
  }
  const echoed = response.headers.get(REQUEST_ID_HEADER) || undefined
  const challenge = response.headers.get('WWW-Authenticate') ?? ''
  return new ApiError(
    response.status,
    response.statusText,
    url,
    method,
    problem.detail,
    problem.code,
    problem.requestId ?? echoed,
    /acr_values="([^"]*)"/.exec(challenge)?.[1] ?? null,
  )
}

export interface RequestOptions {
  /** Overrides the token bridged out of the OIDC session. */
  token?: string | null
  /**
   * Whether a step-up refusal (`step_up_required`) signs in again and retries once. On by default; off for the
   * check that only asks.
   */
  stepUpRetry?: boolean
  /** Serialized here, so every caller sends the same content type. */
  json?: unknown
  /** A multipart body, for the one endpoint that takes a file. */
  form?: FormData
  /**
   * A file sent as the body itself, `application/octet-stream` — for firmware images, which run to
   * hundreds of MB and which the relay streams to disk rather than parsing as multipart.
   */
  binary?: Blob
  /**
   * A CSV document sent as the body itself, `text/csv` — for the pre-registration import, which the
   * relay parses server-side so its per-row errors name the rows the operator actually wrote.
   */
  csv?: string
  /**
   * What to make of the response body.
   *
   * `json` parses it; `file` reads the bytes and the name the server gave them; `text` returns it
   * unparsed, which the PCI compliance diagram needs — that endpoint answers with raw HTML, and
   * `json()` would throw on the first character. Every mutating endpoint here answers with JSON, so
   * there is deliberately no "ignore the body" mode: an option nothing passes is a branch no test
   * can reach. `stream` hands back the body unread, for the one endpoint that never ends
   * (`/events/stream`, Server-Sent Events); pass `timeoutMs: null` with it.
   */
  accept?: 'json' | 'file' | 'text' | 'stream'
  /**
   * Cancels the request: pass React Query's `signal` from the query function so a query that is
   * no longer wanted (the page was left, the key changed) stops its request rather than finishing
   * it for nobody. Cancellation rejects with the `AbortError` React Query expects.
   */
  signal?: AbortSignal
  /**
   * The timeout in milliseconds, or `null` for none.
   *
   * Defaults to {@link DEFAULT_TIMEOUT_MS}, except for transfers — a `form` or `binary` upload or an
   * `accept: 'file'` download — which default to none: a firmware image or a device file over a
   * shop's uplink legitimately takes minutes, and abandoning it at thirty seconds would make large
   * transfers impossible rather than safer.
   */
  timeoutMs?: number | null
}

/**
 * Issues one request.
 *
 * The token defaults to the one bridged out of the OIDC session, so most callers need not pass it;
 * `PermissionsContext` passes its own because it holds the token that gates its query.
 */
export async function request<T>(method: string, url: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await requestOnce<T>(method, url, options)
  } catch (error) {
    // Step-up (Plans/identity-consolidation.md): the relay wants a more recent or stronger sign-in for this. Sign
    // in again — a popup, so the page survives — then ask once more, with the new token.
    const stepUp = getStepUpHandler()
    if (!(error instanceof ApiError) || error.code !== 'step_up_required' || options.stepUpRetry === false || !stepUp)
      throw error
    await stepUp(error.acrValues)
    return requestOnce<T>(method, url, { ...options, token: undefined })
  }
}

async function requestOnce<T>(method: string, url: string, options: RequestOptions): Promise<T> {
  const bearer = options.token ?? getAccessToken()
  const headers: Record<string, string> = { [REQUEST_ID_HEADER]: newRequestId() }
  if (bearer) headers.Authorization = `Bearer ${bearer}`

  let body: BodyInit | undefined
  if (options.json !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.json)
  } else if (options.form) {
    // No Content-Type set, deliberately. The browser must write it itself so that it carries the
    // multipart boundary; a hand-written `multipart/form-data` header has no boundary and the relay
    // rejects it outright with "Missing boundary in Content-Type".
    body = options.form
  } else if (options.binary) {
    // The Blob's own type when it has one — a screenshot is a PNG and says so, which is worth more
    // in a log and an audit than a second name for "bytes".
    headers['Content-Type'] = options.binary.type || 'application/octet-stream'
    body = options.binary
  } else if (options.csv !== undefined) {
    headers['Content-Type'] = 'text/csv; charset=utf-8'
    body = options.csv
  }

  const transfer = options.form !== undefined || options.binary !== undefined || options.accept === 'file'
  const timeoutMs = options.timeoutMs === undefined ? (transfer ? null : DEFAULT_TIMEOUT_MS) : options.timeoutMs

  // A controller rather than AbortSignal.timeout(): the timer is cleared as soon as the response is
  // read, so a finished request leaves nothing behind, and the timeout's own signal is the one
  // thing that tells a timeout apart from the caller cancelling.
  const timeout = timeoutMs === null ? null : new AbortController()
  const timer = timeout ? setTimeout(() => timeout.abort(), timeoutMs!) : undefined
  const signals = [options.signal, timeout?.signal].filter((s): s is AbortSignal => s !== undefined)
  const signal = signals.length > 0 ? anySignal(signals) : undefined

  try {
    const response = await fetch(url, { method, headers, body, signal })

    if (!response.ok) {
      throw await failedResponse(response, url, method)
    }

    if (options.accept === 'text') return (await response.text()) as T
    if (options.accept === 'stream') return response.body as T
    if (options.accept === 'file') {
      return {
        blob: await response.blob(),
        filename: filenameFromDisposition(response.headers.get('Content-Disposition')),
      } as T
    }

    return (await response.json()) as T
  } catch (error) {
    // Only the timeout's own abort becomes a timeout. The caller's cancellation is rethrown as it
    // came, because React Query recognises it and treats the query as cancelled, not failed.
    if (timeout?.signal.aborted && !options.signal?.aborted) {
      throw new ApiTimeoutError(url, method, timeoutMs!)
    }
    throw error
  } finally {
    clearTimeout(timer)
  }
}
